import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getContracts, deleteContract, downloadContract, createContract } from '../api';
import { Plus, FileJson, FolderOpen, RefreshCw, Database } from 'lucide-react';
import ContractCard from './contracts/ContractCard';
import ContractSearch from './contracts/ContractSearch';
import ContractViewModal from './contracts/ContractViewModal';
import ContractExportModal from './contracts/ContractExportModal';
import ContractSettingsModal from './contracts/ContractEditModal';
import CreateContractModal from './contracts/CreateContractModal';

const ContractsManager = ({ addToast, refreshTrigger, onNavigateToComparator, onNavigateToVersions, onContractChanged }) => {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');

    // Undo grace period state
    const [pendingDeletions, setPendingDeletions] = useState(new Set());
    const deletionTimeoutsRef = useRef({});

    // Modal state
    const [viewContract, setViewContract] = useState(null);
    const [exportContract, setExportContract] = useState(null);
    const [editContract, setEditContract] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Load contracts
    const loadContracts = useCallback(async (showRefreshIndicator = false) => {
        if (showRefreshIndicator) setRefreshing(true);

        try {
            const data = await getContracts({ search: searchQuery });
            setContracts(data);
        } catch (error) {
            console.error('Failed to load contracts:', error);
            // Error toast disabled to prevent re-render loops
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [searchQuery]); // Removed addToast from dependencies to prevent loops

    // Separate error display effect
    useEffect(() => {
        // This empty effect helps stabilize the component lifecycle
    }, []); // Run only once on mount

    // Load on mount and when refreshTrigger changes
    useEffect(() => {
        loadContracts();
    }, [loadContracts, refreshTrigger]);

    // Handle search changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadContracts();
        }, 300);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Filter contracts locally for instant feedback
    const filteredContracts = useMemo(() => {
        let result = contracts.filter(c => !pendingDeletions.has(c.id));

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(c =>
                c.name.toLowerCase().includes(query) ||
                (c.description && c.description.toLowerCase().includes(query))
            );
        }

        return result;
    }, [contracts, searchQuery, pendingDeletions]);

    // Group contracts by vendor
    const groupedContracts = useMemo(() => {
        const groups = {};

        filteredContracts.forEach(contract => {
            const vendorName = contract.vendor || 'Ungrouped';
            if (!groups[vendorName]) {
                groups[vendorName] = [];
            }
            groups[vendorName].push(contract);
        });

        // Sort: named vendors first (alphabetically), Ungrouped last
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            if (a === 'Ungrouped') return 1;
            if (b === 'Ungrouped') return -1;
            return a.localeCompare(b);
        });

        return sortedKeys.map(vendor => ({
            vendor,
            contracts: groups[vendor]
        }));
    }, [filteredContracts]);

    // Handlers
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
    }, []);

    const handleViewHistory = useCallback((contract) => {
        if (onNavigateToVersions) {
            onNavigateToVersions(contract.id);
        } else {
            addToast?.('Version history feature requires navigation support', 'info');
        }
    }, [onNavigateToVersions, addToast]);

    const handleExport = useCallback((contract) => {
        setExportContract(contract);
    }, []);

    const handleEdit = useCallback((contract) => {
        setEditContract(contract);
    }, []);

    const handleExportDownload = useCallback(async (contractId, format) => {
        try {
            await downloadContract(contractId, format);
            addToast?.('Contract exported successfully', 'success');
        } catch (error) {
            console.error('Export failed:', error);
            addToast?.('Failed to export contract', 'error');
        }
    }, [addToast]);

    const handleCopy = useCallback(async (contract) => {
        try {
            await navigator.clipboard.writeText(
                JSON.stringify(contract.schema_content, null, 2)
            );
            addToast?.('Schema copied to clipboard', 'success');
        } catch (error) {
            addToast?.('Failed to copy to clipboard', 'error');
        }
    }, [addToast]);

    const handleCompare = useCallback((contract) => {
        if (onNavigateToComparator) {
            onNavigateToComparator(contract.id);
        } else {
            addToast?.('Go to Comparator and select this contract as source', 'info');
        }
    }, [onNavigateToComparator, addToast]);

    const handleDelete = useCallback((contract) => {
        // Optimistic UI hide
        setPendingDeletions(prev => new Set(prev).add(contract.id));

        // Schedule actual deletion
        const timeoutId = setTimeout(async () => {
            try {
                await deleteContract(contract.id);
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(contract.id);
                    return next;
                });
                await loadContracts();
                onContractChanged?.();
            } catch (error) {
                console.error('Delete failed:', error);
                addToast?.('Failed to delete contract', 'error');
                // Revert optimistic hide
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(contract.id);
                    return next;
                });
            }
        }, 2000);

        deletionTimeoutsRef.current[contract.id] = timeoutId;

        addToast?.(`Contract "${contract.name}" deleted`, 'success', {
            duration: 2000,
            action: {
                label: 'Undo',
                onClick: () => {
                    clearTimeout(deletionTimeoutsRef.current[contract.id]);
                    delete deletionTimeoutsRef.current[contract.id];
                    setPendingDeletions(prev => {
                        const next = new Set(prev);
                        next.delete(contract.id);
                        return next;
                    });
                    addToast?.('Deletion undone', 'info');
                }
            }
        });
    }, [loadContracts, addToast, onContractChanged]);

    const handleFork = useCallback(async (contract, forkName) => {
        try {
            await createContract({
                name: forkName,
                schema_content: contract.schema_content,
                description: contract.description ? `Forked from "${contract.name}": ${contract.description}` : `Forked from "${contract.name}"`,
                tags: [...(contract.tags || []), 'forked']
            });
            await loadContracts();
            addToast?.(`Contract forked as "${forkName}"`, 'success');
            onContractChanged?.();
        } catch (error) {
            console.error('Fork failed:', error);
            addToast?.('Failed to fork contract: ' + (error.response?.data?.detail || error.message), 'error');
        }
    }, [loadContracts, addToast, onContractChanged]);

    const handleRefresh = useCallback(() => {
        loadContracts(true);
    }, [loadContracts]);

    // Loading state
    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="flex items-center gap-3 text-[#808080]">
                    <RefreshCw size={20} className="animate-spin" />
                    Loading...
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-white">
                        Data Contracts
                    </h2>
                    <p className="text-sm text-[#808080] mt-1">
                        {contracts.length} contract{contracts.length !== 1 ? 's' : ''} saved
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        <Plus size={16} />
                        New Contract
                    </button>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-400 hover:bg-[#4c4c4c] disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Create Contract Modal */}
            <CreateContractModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => {
                    loadContracts();
                    onContractChanged?.();
                }}
                addToast={addToast}
            />

            {/* Search */}
            <ContractSearch
                onSearch={handleSearch}
                searchValue={searchQuery}
            />

            {/* Modals */}
            <ContractViewModal
                contract={viewContract}
                isOpen={!!viewContract}
                onClose={() => setViewContract(null)}
                onExport={handleExport}
                addToast={addToast}
            />

            <ContractExportModal
                contract={exportContract}
                isOpen={!!exportContract}
                onClose={() => setExportContract(null)}
                onExport={handleExportDownload}
                addToast={addToast}
            />

            <ContractSettingsModal
                contract={editContract}
                isOpen={!!editContract}
                onClose={() => setEditContract(null)}
                addToast={addToast}
            />

            {/* Content */}
            {filteredContracts.length === 0 ? (
                <div className="bg-[#1e1e1e] border-[#3c3c3c] rounded-xl p-5 shadow-sm border border-[#2d2d2d] mb-8">
                    {contracts.length === 0 ? (
                        <>
                            <div className="inline-flex p-4 bg-[#252526] rounded-2xl mb-4">
                                <FolderOpen size={48} className="text-[#808080]" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#cccccc] mb-2">
                                No contracts yet
                            </h3>
                            <p className="text-[#808080] max-w-2xl mb-6auto">
                                Save your first contract from the Comparator view. Contracts let you store
                                baseline schemas to compare against future files.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="inline-flex p-4 bg-[#252526] rounded-2xl mb-4">
                                <FileJson size={48} className="text-[#808080]" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#cccccc] mb-2">
                                No matching contracts
                            </h3>
                            <p className="text-[#808080]">
                                Try adjusting your search criteria.
                            </p>
                            <button
                                onClick={() => { setSearchQuery(''); }}
                                className="mt-4 px-4 py-2 text-sm font-medium text-blue-400 hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                                Clear search
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-6">
                    {groupedContracts.map((group, groupIndex) => (
                        <div key={group.vendor}>
                            {/* Vendor Separator */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-[2px] flex-1 bg-white/20"></div>
                                <span className="text-sm font-bold uppercase tracking-widest px-4 text-white">
                                    {group.vendor}
                                </span>
                                <div className="h-[2px] flex-1 bg-white/20"></div>
                            </div>

                            {/* Contracts Grid */}
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2 items-start">
                                {group.contracts.map(contract => (
                                    <ContractCard
                                        key={contract.id}
                                        contract={contract}
                                        onViewHistory={handleViewHistory}
                                        onExport={handleExport}
                                        onCopy={handleCopy}
                                        onCompare={handleCompare}
                                        onDelete={handleDelete}
                                        onFork={handleFork}
                                        onEdit={handleEdit}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ContractsManager;
